// Cross-runtime SVG focus helpers shared by the keyboard-navigation code
// in `OrgGraph` (force-directed graph) and `OrgTree` (top-down tree).
//
// Both views need to move actual DOM focus to a `<g data-upn="...">` node
// after arrow-key navigation so subsequent keypresses originate from the
// newly-focused node. The `CSS.escape` API is part of the CSSOM and not
// guaranteed to exist in every runtime where `window` does (older
// webviews, JSDOM in certain modes), so we wrap it in a feature check
// with an attribute-iteration fallback.
//
// Extracting these utilities here avoids the new-code duplication that
// would otherwise be flagged by SonarCloud when both consumers carry a
// near-identical copy of the same logic.

/**
 * Returns a CSS.escape-encoded attribute value, or `null` if `CSS.escape`
 * is not available in this runtime. Callers should fall back to non-selector
 * lookup when this returns null.
 */
export function escapeAttributeValue(value: string): string | null {
  const cssApi = (globalThis as { CSS?: { escape?: (input: string) => string } }).CSS;
  if (cssApi && typeof cssApi.escape === 'function') {
    return cssApi.escape(value);
  }
  return null;
}

/**
 * CSS.escape-free fallback for locating an element by its `data-upn` value.
 * Walks all `[data-upn]` nodes under `root` and returns the first whose
 * attribute matches `upn` exactly. Used when `CSS.escape` is unavailable so
 * keyboard focus traversal still works.
 */
export function findByDataUpn(root: SVGSVGElement, upn: string): Element | null {
  const candidates = root.querySelectorAll<SVGElement>('[data-upn]');
  for (const el of Array.from(candidates)) {
    if (el.dataset.upn === upn) return el;
  }
  return null;
}
